import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Stack, Typography, IconButton, Tabs, Tab,
  Skeleton, Alert, Button, Paper, useTheme
} from '@mui/material';
import {
  ArrowBack, GroupWork, MenuBook, People, History as HistoryIcon,
  ArrowForward, OpenInNew, School
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import StatusChip from '../../components/common/StatusChip';
import SectionsTab from '../../components/sections/SectionsTab';
import CurriculumTab from '../../components/curriculum/CurriculumTab';
import TeachingLoadsTab from '../../components/teaching-loads/TeachingLoadsTab';

type TabKey = 'sections' | 'curriculum' | 'teaching' | 'enrollments' | 'promotions';
const TAB_LIST: { key: TabKey; label: string; icon: React.ReactElement }[] = [
  { key: 'sections',    label: 'Sections',         icon: <GroupWork    fontSize="small" /> },
  { key: 'curriculum',  label: 'Curriculum',       icon: <MenuBook     fontSize="small" /> },
  { key: 'teaching',    label: 'Teaching Loads',   icon: <School       fontSize="small" /> },
  { key: 'enrollments', label: 'Enrollments',      icon: <People       fontSize="small" /> },
  { key: 'promotions',  label: 'Promotion Runs',   icon: <HistoryIcon  fontSize="small" /> }
];

const SessionDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const tabParam = (searchParams.get('tab') as TabKey) || 'sections';
  const [tab, setTab] = useState<TabKey>(tabParam);

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await sessionsApi.get(id);
      setSession(s);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load session', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => { if (id) load(); }, [id, load]);

  const changeTab = (next: TabKey) => {
    setTab(next);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    });
  };

  if (loading || !session) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={32} />
        <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <IconButton size="small" onClick={() => navigate('/app/settings/sessions')} aria-label="Back to sessions">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Typography variant="h5" fontWeight={700} color="text.primary">{session.name}</Typography>
            <StatusChip status={{ kind: 'session', value: session.status }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(session.startDate), 'MMM d, yyyy')} → {format(new Date(session.endDate), 'MMM d, yyyy')}
          </Typography>
        </Box>
      </Stack>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => changeTab(v)}
          sx={{
            px: 1,
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 44,
              gap: 0.75
            }
          }}
        >
          {TAB_LIST.map(t => (
            <Tab
              key={t.key}
              value={t.key}
              icon={t.icon}
              iconPosition="start"
              label={t.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab body */}
      {tab === 'sections'    && <SectionsTab sessionId={session._id} />}
      {tab === 'curriculum'  && <CurriculumTab sessionId={session._id} />}
      {tab === 'teaching'    && <TeachingLoadsTab sessionId={session._id} sessionName={session.name} />}
      {tab === 'enrollments' && <EnrollmentsTabRedirect sessionId={session._id} />}
      {tab === 'promotions'  && <PromotionsTabRedirect sessionId={session._id} />}
    </Box>
  );
};

// Lightweight placeholders that pivot to the dedicated pages. F3 / F4 build
// the full UI; these keep the tab structure complete without forking the work.
const EnrollmentsTabRedirect: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const navigate = useNavigate();
  return (
    <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
      <People sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
      <Typography variant="h6" fontWeight={600}>Manage enrollments</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 480, mx: 'auto' }}>
        The Enrollments page lets you add students, transfer them between classes mid-session,
        bulk-import a CSV, and review the roster by class or section.
      </Typography>
      <Button
        variant="contained"
        disableElevation
        endIcon={<ArrowForward />}
        onClick={() => navigate(`/app/enrollments?sessionId=${sessionId}`)}
      >
        Open Enrollments
      </Button>
    </Paper>
  );
};

const PromotionsTabRedirect: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const navigate = useNavigate();
  return (
    <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
      <HistoryIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
      <Typography variant="h6" fontWeight={600}>Promotion runs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 480, mx: 'auto' }}>
        Promote students to the next session — class by class, with smart defaults and a per-row preview.
      </Typography>
      <Button
        variant="contained"
        disableElevation
        endIcon={<OpenInNew />}
        onClick={() => navigate(`/app/promotions/new?sourceSessionId=${sessionId}`)}
      >
        Start a promotion run
      </Button>
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" variant="outlined" sx={{ textAlign: 'left' }}>
          Tip: existing runs for this session will appear here once the Promotion Wizard is built (next phase).
        </Alert>
      </Box>
    </Paper>
  );
};

export default SessionDetailPage;
